import React from 'react';
import { toast as reactToast, Slide } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.min.css';
import { tv } from 'tailwind-variants';
import cn from '@/utils/cn';

type PositionOptions =
  | 'top-right'
  | 'top-center'
  | 'top-left'
  | 'bottom-right'
  | 'bottom-center'
  | 'bottom-left';

type ToastId = number | string;

interface ToastShowOptions {
  autoClose?: number | false;
  className?: string;
  closeButton?: React.ReactNode | false;
  closeOnClick?: boolean;
  onClose?: () => void;
  onOpen?: () => void;
  position?: PositionOptions;
  toastId?: ToastId;
}

// Important: react-toastify's stylesheet loads after Tailwind (components import it, the pack
// requires Tailwind first) and paints .Toastify__toast with its own font, padding, radius,
// min-height and pointer cursor at the same specificity, so the contested classes win explicitly.
const toastVariants = tv({
  base: [
    '!tw-rounded-lg tw-bg-white !tw-shadow-lg !tw-font-tci tw-text-sm tw-text-very-dark-grey',
    '!tw-min-h-0 !tw-cursor-default !tw-p-4 tw-flex tw-items-center tw-gap-3',
  ],
});

const DefaultCloseButton = ({ closeToast }: { closeToast?: () => void }) => (
  <button
    aria-label="Close notification"
    className={cn(
      'tw-ml-auto tw-shrink-0 tw-bg-transparent tw-border-none tw-cursor-pointer',
      'tw-text-dark-grey hover:tw-text-very-dark-grey tw-p-0 tw-text-lg tw-leading-none',
    )}
    type="button"
    onClick={closeToast}
  >
    &#x2715;
  </button>
);

const show = (content: React.ReactNode, options: ToastShowOptions = {}): ToastId => {
  const {
    autoClose = 5000,
    className,
    closeButton,
    closeOnClick = false,
    onClose,
    onOpen,
    position = 'bottom-right',
    toastId,
  } = options;

  const resolvedCloseButton = closeButton === false
    ? false
    : closeButton ?? <DefaultCloseButton />;

  return reactToast(content, {
    autoClose,
    bodyClassName: 'tw-p-0 tw-m-0 tw-flex-1',
    className: cn(toastVariants(), className),
    closeButton: resolvedCloseButton,
    closeOnClick,
    draggable: false,
    hideProgressBar: true,
    onClose,
    onOpen,
    position,
    toastId,
    transition: Slide,
  });
};

const toast = {
  dismiss: reactToast.dismiss,
  isActive: reactToast.isActive,
  show,
};

export default toast;
